import React from 'react';
import './ProductSectionCard.css';

export interface ProductSectionCardProps {
  /** Section title (platform name) */
  title: string;
  /** Optional badge rendered next to the title (e.g. `<ComingSoonBadge />`) */
  badge?: React.ReactNode;
  /** When true the title uses the muted colour of an unavailable platform */
  muted?: boolean;
  /** Section content — product cards */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
}

/**
 * ProductSectionCard
 *
 * White elevated column that groups the products of one platform: title,
 * optional badge and a vertical list of cards. Figma nodes `1601:13673`
 * (Plataforma Data Trust), `1601:13929` (Portal de Identidades Digitais) and
 * `1601:14101` (Plataforma idwall).
 *
 * @example
 * ```tsx
 * <ProductSectionCard title={t('Data Trust Platform')}>
 *   <QuickSearchInput />
 *   <ProductRowCard {...props} />
 * </ProductSectionCard>
 * ```
 *
 * @since 1.0.0
 * @category Organisms
 */
export const ProductSectionCard: React.FC<ProductSectionCardProps> = ({
  title,
  badge,
  muted = false,
  children,
  className = '',
}) => (
  <section
    className={`product-section-card ${className}`.trim()}
    aria-label={title}
  >
    <header className="product-section-card__header">
      <h4
        className={`product-section-card__title ${
          muted ? 'product-section-card__title--muted' : ''
        }`.trim()}
      >
        {title}
      </h4>
      {badge}
    </header>
    <div className="product-section-card__body">{children}</div>
  </section>
);

export default ProductSectionCard;
